import React from 'react';
import { useSelector } from 'react-redux';
import { Forms, Loading, SystemText } from 'e4shared';
import Filters from '../_filters';
import GroupPicker from 'components/admin/forms/groupPicker';
import Chart from './chart';
import WidgetBase, { groupSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import Recommends from 'views/user/1-2-1s/components/predictiveModelWrapper/recommendations';

export const PredictiveModel = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});

    const defaults = { show_For: true, period: 'Y2', period1: 'M3' };

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const reload = () => {
        widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const height = defaultProps.height(item);

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);

        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/Leaderboard/Fields/Group/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <div className="chart" style={{ height: `${height}px` }}>
                <Chart data={data} item={item} id={`chart-${item.id}`} reload={reload} />
            </div>
        </div>
    );

    const filters = values ? (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <Forms.Buttons
                label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/FY/Label')}
                help={SystemText('Dashboard/Widgets/PredictiveModel/Fields/FY/Help')}
            >
                <button
                    className={`btn btn-${values.period === 'Y1' ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('Y1', 'period')}
                >
                    {SystemText('Dashboard/Widgets/PredictiveModel/Fields/FY/This')}
                </button>
                <button
                    className={`btn btn-${values.period !== 'Y1' ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('Y2', 'period')}
                >
                    {SystemText('Dashboard/Widgets/PredictiveModel/Fields/FY/Next')}
                </button>
            </Forms.Buttons>
            <Forms.Buttons
                label={SystemText('Dashboard/Widgets/PredictiveModel/Fields/Months/Label')}
                help={SystemText('Dashboard/Widgets/PredictiveModel/Fields/Months/Help')}
            >
                <button
                    className={`btn btn-${values.period1 === 'M1' ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('M1', 'period1')}
                >
                    {SystemText('Dashboard/Widgets/PredictiveModel/Fields/Months/1')}
                </button>
                <button
                    className={`btn btn-${!['M1', 'M6', 'M12'].some(x => x === values.period1) ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('M3', 'period1')}
                >
                    {SystemText('Dashboard/Widgets/PredictiveModel/Fields/Months/3')}
                </button>
                <button
                    className={`btn btn-${values.period1 === 'M6' ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('M6', 'period1')}
                >
                    {SystemText('Dashboard/Widgets/PredictiveModel/Fields/Months/6')}
                </button>
                <button
                    className={`btn btn-${values.period1 === 'M12' ? 'primary' : 'default'}`}
                    onClick={() => updateProperty('M12', 'period1')}
                >
                    {SystemText('Dashboard/Widgets/PredictiveModel/Fields/Months/12')}
                </button>
            </Forms.Buttons>
        </Filters>
    ) : null;

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recLoading ? <Loading /> : <Recommends recommendationData={recommendationData} />}
            id={id}
            help={SystemText('Dashboard/Widgets/PredictiveModel/Help', (values.period1 || 'M3').replace('M', ''))}
            hideConfig={defaultProps.company && !user.administrator}
            extra={item.extra}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default PredictiveModel;
